import CryptoJS from "crypto-js";
import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import {
	FiltroEmbarqueCompleto,
	FiltroEmbarqueCompletoEnum,
} from "../models/FiltroEmbarqueCompleto.ts";
import { emptyFiltroEmbarque } from "./ContextConstants.ts";
import { useProfile } from "./ProfileContext.tsx";
import { useToast } from "./ToastContext.tsx";

const deepEqual = (obj1: any, obj2: any): boolean => {
	if (obj1 === obj2) return true;
	if (
		typeof obj1 !== "object" ||
		typeof obj2 !== "object" ||
		obj1 === null ||
		obj2 === null
	) {
		return false;
	}
	const keys1 = Object.keys(obj1);
	const keys2 = Object.keys(obj2);
	if (keys1.length !== keys2.length) return false;
	return keys1.every((key) => deepEqual(obj1[key], obj2[key]));
};

interface FiltroContextType {
	ordenarPor: string;
	filtroEmbarque: FiltroEmbarqueCompleto;
	setFiltro: (key: FiltroEmbarqueCompletoEnum, value: any) => void;
	clearFiltros: () => void;
	setOrdenarPor: (value: string) => void;
	isFiltroModified: () => boolean;
	qtFiltrosAplicados: number;
	copyFiltrosToClipboard: () => void;
	aplicaFiltrosCopiado: ({
		buscarViagensClipboard,
	}: {
		buscarViagensClipboard?: (filtros: FiltroEmbarqueCompleto) => void;
	}) => Promise<void>;
	normalizarFiltroEmbarque: (
		filtroEmbarque: FiltroEmbarqueCompleto
	) => FiltroEmbarqueCompleto;
}

interface FiltroProviderProps {
	children: ReactNode;
}

const FiltroContext = createContext<FiltroContextType | undefined>(undefined);

export const ClipboardSemAutorizacaoMessage = () => {
	return (
		<div>
			<p>Permissão negada para copiar filtro.</p>
			<p>Verifique as permissões do navegador.</p>
			<p>
				Ou clique{" "}
				<a
					href="https://www.youtube.com/watch?v=cr-uWIvDnsA"
					target="_blank"
					rel="noopener noreferrer"
				>
					aqui
				</a>{" "}
				para mais informações.
			</p>
		</div>
	);
};

export const FiltroProvider = ({ children }: FiltroProviderProps) => {
	const { contextoEmbarque } = useProfile();
	const { showToast } = useToast();

	const [filtroEmbarque, setFiltroEmbarque] = useState<FiltroEmbarqueCompleto>({
		...emptyFiltroEmbarque,
		[FiltroEmbarqueCompletoEnum.Contexto]: contextoEmbarque,
	});
	const [ordenarPor, setOrdenarPor] = useState<string>("previsaoEmbarque");

	const isFiltroModified = useCallback(() => {
		const {
			[FiltroEmbarqueCompletoEnum.Contexto]: _,
			...filteredCurrentFiltro
		} = filtroEmbarque;
		const {
			[FiltroEmbarqueCompletoEnum.Contexto]: __,
			...filteredInitialFiltro
		} = emptyFiltroEmbarque;

		return !deepEqual(filteredCurrentFiltro, filteredInitialFiltro);
	}, [filtroEmbarque, emptyFiltroEmbarque]);

	const contadorFiltrosAplicados = (currentFiltro: FiltroEmbarqueCompleto, initialFiltro: FiltroEmbarqueCompleto) => {
		let count = 0;

		Object.keys(currentFiltro).forEach((key) => {
            const typedKey = key as keyof FiltroEmbarqueCompleto;
			if (typedKey === FiltroEmbarqueCompletoEnum.Contexto) {
				return;
			}

			const currentValue = currentFiltro[typedKey];
			const initialValue = initialFiltro[typedKey];

			if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
				if (
					currentValue.length !== initialValue.length ||
					!currentValue.every((val, idx) => val === initialValue[idx])
				) {
					count++;
				}
			} else if (currentValue !== initialValue) {
				count++;
			}
		});

		return count;
	};

	const normalizarFiltroEmbarque = useCallback(
		(filtroEmbarque: FiltroEmbarqueCompleto) => {
			const filtro = Object.keys(filtroEmbarque).reduce((acc, key) => {
            const typedKey = key as keyof FiltroEmbarqueCompleto;
				const value = filtroEmbarque[typedKey];

				if (
					key === FiltroEmbarqueCompletoEnum.PrevisaoEmbarqueInicio ||
					key === FiltroEmbarqueCompletoEnum.PrevisaoEmbarqueFim ||
					key === FiltroEmbarqueCompletoEnum.PrevisaoEntregaInicio ||
					key === FiltroEmbarqueCompletoEnum.PrevisaoEntregaFim
				) {
					acc[key] = value ? value.split("T")[0] : null;
				} else {
					acc[key] = value;
				}

				return acc;
			}, {});

			filtro[FiltroEmbarqueCompletoEnum.Contexto] = "todos";
			return filtro;
		},
		[]
	);

	const qtFiltrosAplicados = useMemo(() => {
		return contadorFiltrosAplicados(filtroEmbarque, emptyFiltroEmbarque);
	}, [filtroEmbarque, emptyFiltroEmbarque]);

	const handleUpdateFiltro = useCallback((key, value) => {
		setFiltroEmbarque((prev) => ({
			...prev,
			[key]: value,
		}));
	}, []);

	const clearFiltros = useCallback(() => {
		setFiltroEmbarque({
			...emptyFiltroEmbarque,
			[FiltroEmbarqueCompletoEnum.Contexto]: contextoEmbarque,
		});
		setOrdenarPor("unidEmbarque");
	}, [contextoEmbarque]);

	const copyFiltrosToClipboard = useCallback(() => {
		if (!document.hasFocus()) {
			console.warn("Documento não está em foco. Tentando focar...");
			window.focus();
		}
		navigator.permissions
			.query({ name: "clipboard-write" as PermissionName })
			.then((permissionStatus) => {
				if (permissionStatus.state === "denied") {
					showToast(ClipboardSemAutorizacaoMessage(), "error");
					return;
				}
				const filtrosAplicados = JSON.stringify(filtroEmbarque, null, 2);

				const encryptedData = CryptoJS.AES.encrypt(
					filtrosAplicados,
					process.env.REACT_APP_CRYPTO_FILTER_KEY!
				).toString();

				navigator.clipboard
					.writeText(encryptedData)
					.then(() => {
						showToast("Filtros copiados com sucesso!", "success");
					})
					.catch((err) => {
						showToast(
							"Erro ao copiar filtros para a área de transferência: " + err,
							"error"
						);
					});

				return permissionStatus;
			});
	}, [filtroEmbarque, showToast]);

	const aplicaFiltrosCopiado = async ({
		buscarViagensClipboard,
	}: {
		buscarViagensClipboard?: (filtros: FiltroEmbarqueCompleto) => void;
	}): Promise<void> => {
		try {
			if (!document.hasFocus()) {
				console.warn("Documento não está em foco. Tentando focar...");
				window.focus();
			}

			const permissionStatus = await navigator.permissions.query({
				name: "clipboard-read" as PermissionName,
			});

			if (permissionStatus.state === "denied") {
				showToast(ClipboardSemAutorizacaoMessage(), "error");
				return;
			}

			const clipboardText = await navigator.clipboard.readText();
			const decryptedData = CryptoJS.AES.decrypt(
				clipboardText,
				process.env.REACT_APP_CRYPTO_FILTER_KEY!
			).toString(CryptoJS.enc.Utf8);

			const parsedJson: FiltroEmbarqueCompleto = JSON.parse(decryptedData);
			setFiltroEmbarque(parsedJson);
			buscarViagensClipboard?.(parsedJson);
		} catch (error) {
			console.error("Erro ao aplicar os filtros copiados:", error);
		}
	};

	const contextValue = useMemo(
		() => ({
			filtroEmbarque,
			setFiltro: handleUpdateFiltro,
			clearFiltros,
			ordenarPor,
			setOrdenarPor,
			isFiltroModified,
			qtFiltrosAplicados,
			copyFiltrosToClipboard,
			aplicaFiltrosCopiado,
			normalizarFiltroEmbarque,
		}),
		[
			filtroEmbarque,
			handleUpdateFiltro,
			clearFiltros,
			ordenarPor,
			setOrdenarPor,
			isFiltroModified,
			qtFiltrosAplicados,
			aplicaFiltrosCopiado,
			normalizarFiltroEmbarque,
		]
	);

	return (
		<FiltroContext.Provider value={contextValue}>
			{children}
		</FiltroContext.Provider>
	);
};

export const useFiltro = () => {
	const context = useContext(FiltroContext);
	if (!context) {
		throw new Error("useFiltro must be used within a FiltroProvider");
	}
	return context;
};
